// Usage: anytime an element with the video ad class appears on the page, this script
// will detect it and initialize a video ad.
//
// When the element is no longer visible or is removed from the DOM, it should apply styling
// to make the ad appear in a fixed position, while also appending it to document body.
//
// When the element is visible or re-added into the DOM, the ad should be appended to
// element and fill the size of the container.
//
// All of the business logic for video ads should be self-contained in here.

import EventEmitter from "event-lite";

import { IS_APP, IS_DEV, IS_TESTING } from "@/__main__/constants.mjs";
import router from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import type { VideoAdData } from "@/feature-ads/constants/constants.mjs";
import {
  VIDEO_AD_CLASS,
  VIDEO_AD_DEFAULT_DATA,
  VIDEO_AD_Z_INDEX,
} from "@/feature-ads/constants/constants.mjs";
import { EVENT_TRACK_VIDEO_PROVIDER } from "@/feature-ads/constants/events.mjs";
import type { AdsConfig } from "@/feature-ads/data-model-ads-static.mjs";
import AdsStatic from "@/feature-ads/data-model-ads-static.mjs";
import fetchRemoteAdConfig from "@/feature-ads/fetch-remote-ad-config.mjs";
import { getMemoryUsage, isAppVisible } from "@/feature-ads/util.mjs";
import { checkPageVisibility } from "@/feature-ads/util/check-page-visibility.mjs";
import { promiseTimeout } from "@/feature-ads/util/promise-timeout.mjs";
import { VIDEO_ADITUDE } from "@/feature-ads/video-aditude.mjs";
import { VIDEO_EXCO } from "@/feature-ads/video-exco.mjs";
import { VIDEO_VIDAZOO } from "@/feature-ads/video-vidazoo.mjs";
import { svg as closeIcon } from "@/inline-assets/close.svg";
import overlayRefs from "@/shared/OverlayContainerWithAd.refs.jsx";
import { devError, devLog } from "@/util/dev.mjs";
import { CONTAINER_ID } from "@/util/exit-transitions.mjs";
import globals from "@/util/global-whitelist.mjs";
import hasQueryString from "@/util/has-query-string.mjs";
import { safeAppend } from "@/util/helpers.mjs";
import nextFrame from "@/util/next-frame.mjs";
import rangeBucket from "@/util/range-bucket.mjs";
import makeStrictKeysObject from "@/util/strict-keys-object.mjs";
import { fetchCountryCode } from "@/util/use-country-code.mjs";

let refreshHandle: Timeout;
let providerKey = null;
let isIntersectingState: boolean;
// these vendors have forced my paw
const MAX_Z_INDEX = Math.pow(2, 31) - 1;

const observer =
  typeof MutationObserver !== "undefined"
    ? new MutationObserver(observerCallback)
    : null;

const activeRef = {
  active: null as HTMLElement | null,
  scroller: null as HTMLElement | null,
  placeholder: null as HTMLElement | null,
  close: null as HTMLElement | null,
};
const containerObserverMap = new WeakMap();
const placementParentMap = new WeakMap();

export const CONFIG = makeStrictKeysObject({
  SCROLL_VIEW_SELECTOR: `#${CONTAINER_ID}`,
  SHOULD_REFRESH: true,
  REFRESH_MS: 5 * 60 * 1000, // 5 minutes
  MIN_REFRESH: 1 * 60 * 1000, // 1 minute
});

export const events = new EventEmitter();
export const EVENT_AD_OPEN = "EVENT_AD_OPEN";
export const EVENT_AD_CLOSE = "EVENT_AD_CLOSE";

export function startObserving() {
  if (!observer) return;
  observer.observe(globals.document.body, {
    subtree: true,
    childList: true,
    // It is not required to listen to attributes since we will assume that
    // once an ad element is present, it does not change after initialization.
  });
  devLog("[video-ads] observer.observe");
}

export function stopObserving() {
  if (!observer) return;
  observer.disconnect();
  devLog("[video-ads] observer.disconnect");
}

// wrapper for safely appending a placement to its parent and handling provider callback
function appendPlacement(parent: HTMLElement, placement: HTMLElement) {
  if (safeAppend(parent, placement))
    VIDEO_PROVIDER?.onPlacementAppend?.(parent, placement);
}

async function observerCallback(mutations) {
  for (const { addedNodes, removedNodes } of mutations) {
    for (const addedNode of addedNodes) {
      if (addedNode.nodeType !== Node.ELEMENT_NODE) continue;
      const node = addedNode.querySelector(`.${VIDEO_AD_CLASS}`) || addedNode;
      if (!node.classList.contains(VIDEO_AD_CLASS)) continue;
      await CHOOSE_VIDEO_PROVIDER_PROMISE;

      VIDEO_PROVIDER?.init();
      const placement = activeRef.active || (await initializePlacement(node));
      if (!placement) {
        node.remove(); // remove the placeholder
        continue;
      }
      placementParentMap.set(placement, node);

      if (!node.dataset.float) {
        // Need to set this prior to appending, otherwise it will expand the container and move the overlay
        placement.style.position = "absolute";
        appendPlacement(node, placement);
        positionAdInContainer(placement, node);
      } else {
        // This is a slight complication, appending to the scroll view is necessary.
        const scrollView = globals.document.querySelector<HTMLElement>(
          CONFIG.SCROLL_VIEW_SELECTOR,
        );
        activeRef.scroller = scrollView || globals.document.body;

        appendPlacement(activeRef.scroller, placement);
        if (!VIDEO_PROVIDER?.vendorIntersectionFloat || IS_TESTING)
          attachIntersectionObserver(placement, node);
        positionAdRelativeToContainer(placement, node);
      }
      // report ads provider only when the ads element is inserted into dom
      reportProviderEvent();

      activeRef.active = placement;
      overlayRefs.activeRef = activeRef;
      activeRef.placeholder = node;
      if (IS_TESTING) placement.dataset.testid = node.dataset.pageMacro;
    }

    for (const removedNode of removedNodes) {
      if (removedNode.nodeType !== Node.ELEMENT_NODE) continue;
      const node =
        removedNode.querySelector(`.${VIDEO_AD_CLASS}`) || removedNode;
      if (node.classList.contains(VIDEO_AD_CLASS) && activeRef.active) {
        if (node.dataset.float) {
          positionAdFixed(activeRef.active);
        }
      }
    }
  }

  refreshPosition();
}

const refreshPosition = (placement = activeRef.active) => {
  if (!placement) return;
  const container = globals.document.querySelector<HTMLElement>(
    `.${VIDEO_AD_CLASS}`,
  );
  if (!container) {
    VIDEO_PROVIDER?.onNavFloat();
    if (VIDEO_PROVIDER?.vendorNavFloat) return;
    positionAdFixed(placement, false);
    return;
  }
  const shouldEnclose = !container.dataset.float;
  const isIntersect =
    !shouldEnclose &&
    (VIDEO_PROVIDER?.vendorIntersectionFloat || isIntersectingState);
  const skipIntersect =
    shouldEnclose || VIDEO_PROVIDER?.vendorIntersectionFloat;

  if (isIntersect) positionAdRelativeToContainer(placement, container);
  else if (!skipIntersect) positionAdFixed(placement);
};
export const onResize = () => {
  refreshPosition();
};
let VIDEO_PROVIDER = null;

const VIDEO_PROVIDER_EXPERIMENTS = {
  exco: VIDEO_EXCO,
  vidazoo: VIDEO_VIDAZOO,
  aditude: VIDEO_ADITUDE,
};

const CHOOSE_VIDEO_PROVIDER_PROMISE = chooseVideoProvider();

async function chooseVideoProvider() {
  let adConfig: AdsConfig, countryCode: string | undefined;
  try {
    [adConfig, countryCode] = await promiseTimeout(
      Promise.all([
        fetchRemoteAdConfig(),
        fetchCountryCode({ skipSafetyCheck: true }),
      ]),
      3000,
      "chooseVideoProvider",
    );
  } catch (error) {
    devError("[video-ads]", error);
    adConfig = AdsStatic.default;
  }
  devLog("[video-ads] country code", countryCode);
  // let's prevent showing video here, so we won't report atvp.
  if (
    (IS_APP && !adConfig.videoEnabled) ||
    (!IS_APP && !adConfig.videoEnabledWeb)
  ) {
    devLog("[video-ads] video ads disabled");
    return;
  }

  const providers = adConfig.videoProviderExperiments.providers;
  // transform the provider list into a range bucket
  const rangeBucketProviders: [number, string][] = providers.map((p) => [
    p.threshold,
    p.provider,
  ]);
  // get random provider based on reached threshold
  providerKey = rangeBucket(rangeBucketProviders)[Math.random()];
  const provider = VIDEO_PROVIDER_EXPERIMENTS[providerKey];

  if (provider) {
    // @ts-ignore
    VIDEO_PROVIDER = provider;
    devLog("[video-ads] Using Provider", providerKey);
  }

  if (hasQueryString("__BLITZ_ADITUDE__")) {
    // @ts-ignore
    VIDEO_PROVIDER = VIDEO_ADITUDE;
    providerKey = "aditude";
    devLog("[video-ads] Forcing Aditude");
  }
  if (hasQueryString("__BLITZ_EXCO__")) {
    VIDEO_PROVIDER = VIDEO_EXCO;
    providerKey = "exco";
    devLog("[video-ads] Forcing Ex.co");
  }
  if (hasQueryString("__BLITZ_VIDAZOO__")) {
    VIDEO_PROVIDER = VIDEO_VIDAZOO;
    providerKey = "vidazoo";
    devLog("[video-ads] Forcing Vidazoo");
  }
}

async function reportProviderEvent() {
  if (!providerKey) return;
  const ram = await getMemoryUsage();
  eventBus.emit(EVENT_TRACK_VIDEO_PROVIDER, {
    provider: providerKey,
    ram,
  });
}

async function initializePlacement(element: HTMLElement) {
  clearTimeout(refreshHandle);

  // verify visibility
  // TODO: this is the safest possible configuration, which does not show the ad
  // * at the earliest possible point. This is a stopgap for bad metric @lyricwulf
  const isVisible = await isAppVisible();
  if (!isVisible) return;

  const placement = VIDEO_PROVIDER?.initializePlacement(element);
  if (!placement) return;

  events.emit(EVENT_AD_OPEN);

  // refresh logic
  if (CONFIG.SHOULD_REFRESH) {
    let refreshInterval = Math.max(CONFIG.REFRESH_MS, CONFIG.MIN_REFRESH);
    if (IS_TESTING) {
      placement.dataset.testid = element.dataset.pageMacro;
      const testInterval = parseFloat(
        router.route.state.testinterval as string,
      );
      if (!isFinite(testInterval)) return placement;
      refreshInterval = testInterval;
    }
    refreshHandle = setTimeout(async () => {
      // do not refresh if the page is not visible.
      // ideally this should be done in the `initializePlacement` phase,
      // but this is easier for now
      await checkPageVisibility();
      const originalParent = activeRef.active?.parentElement;
      if (!originalParent) return devLog("[video-ads] refresh failed");
      devLog("[video-ads] refresh");
      // replace video element
      activeRef.active.remove();

      const placement = await initializePlacement(activeRef.placeholder);
      if (!placement) return;
      activeRef.active = placement;

      // chores
      placementParentMap.set(placement, activeRef.placeholder);
      appendPlacement(originalParent, placement);
      attachIntersectionObserver(placement, activeRef.placeholder);
      positionAdRelativeToContainer(placement, activeRef.placeholder);
      if (IS_TESTING) clearTimeout(refreshHandle);
    }, refreshInterval);
  }

  // dev helpers, helps to see with no real ads
  if (IS_DEV) {
    placement.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
    placement.style.border = "1px solid white";
    placement.style.transition = "border 0.5s";
    nextFrame(
      // indicates refreshes
      () => (placement.style.border = "1px solid transparent"),
    );
  }

  return placement;
}

const CLOSE_TIMEOUT = 2000;
export function destroyAd(reason: string) {
  devLog(`[video-ads] destroyAd: ${reason}`);
  clearTimeout(refreshHandle);
  // emergency close in case internal close handlers fail
  const elements = [activeRef.active, activeRef.placeholder];
  setTimeout(() => elements.forEach((el) => el?.remove()), CLOSE_TIMEOUT);
  // run provider destroy handler
  VIDEO_PROVIDER?.onDestroy(activeRef);
  // cleanup anything unhandled
  for (const el of ["active", "placeholder", "close"]) {
    if (activeRef[el]) activeRef[el].remove();
    activeRef[el] = undefined;
  }
  events.emit(EVENT_AD_CLOSE);
}

const CLOSE_BTN_CLASS = "⚡video-close-btn";
export const VIDEO_AD_CSS = `
  .${CLOSE_BTN_CLASS} {
    position: absolute;
    z-index: ${MAX_Z_INDEX};
    padding: 0.125rem;
    top: 0.25rem;
    right: 0.25rem;
    cursor: pointer;
    background: black;
    border: solid gray;
    border-width: 1px;
    border-radius: 50%;
    transition: transform var(--transition), border-radius var(--transition);
  }
  .${CLOSE_BTN_CLASS} > svg {
    height: var(--sp-4);
  }

  .${CLOSE_BTN_CLASS}:hover {
    border-color: lightgrey;
    border-radius: 0.25rem;
    transform: scale(1.35);
  }
  .${CLOSE_BTN_CLASS}:active {
    border-color: white;
  }
`;
export function showCloseButton({
  removable = false,
  force = false,
  placement = activeRef.active,
} = {}) {
  if (!placement) return;
  if (VIDEO_PROVIDER?.vendorCloseButton && !force) return;
  const el = (activeRef.close ||= createCloseButton());
  safeAppend(activeRef.active, el);
  if (!el.hasAttribute("data-show")) el.setAttribute("data-show", ""); // test runner is picky
  if (!removable) activeRef.close.removeAttribute("data-removable");
  if (force) el.removeAttribute("data-no-close");
  updateCloseVisibility(el);
}

function createCloseButton() {
  const el = globals.document.createElement("button");
  el.className = CLOSE_BTN_CLASS;
  el.innerHTML = closeIcon;
  el.onclick = () => destroyAd("close button");
  el.setAttribute("data-removable", "");
  el.setAttribute("data-no-close", "");
  return el;
}

export function hideCloseButton({ force = false } = {}) {
  const el = (activeRef.close ||= createCloseButton());
  el.removeAttribute("data-show");
  if (force) el.setAttribute("data-no-close", "");
  updateCloseVisibility(el);
}

function updateCloseVisibility(el) {
  if (el.hasAttribute("data-show")) el.style.visibility = "visible";
  else el.style.visibility = "hidden";

  if (!el.hasAttribute("data-removable")) el.style.visibility = "visible";

  if (el.hasAttribute("data-no-close")) el.style.visibility = "hidden";
}

function attachIntersectionObserver(placement, container) {
  if (containerObserverMap.get(container)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      if (!globals.document.body.contains(container)) {
        observer.disconnect();
        return;
      }
      const [{ isIntersecting }] = entries;
      isIntersectingState = isIntersecting;
      if (isIntersecting && container)
        positionAdRelativeToContainer(placement, container);
      else positionAdFixed(placement, false);
    },
    {
      threshold: 0.51,
    },
  );
  observer.observe(container);
  containerObserverMap.set(container, observer);
}

function positionAdFixed(placement, forceClose = false) {
  const adElement = globals.document.querySelector<HTMLElement>(
    `.${VIDEO_AD_CLASS}`,
  );
  // dont set any style if there is no ads container
  if (!adElement) return;
  placement.style.position = "fixed";
  placement.style.zIndex = "1000";
  placement.style.top = "auto";
  placement.style.left = "auto";
  placement.style.bottom = "var(--sp-2)";
  placement.style.right = "var(--sp-2)";
  placement.style.width = `${
    adElement?.clientWidth || VIDEO_AD_DEFAULT_DATA.width
  }px`;
  placement.style.aspectRatio =
    adElement?.style.aspectRatio || VIDEO_AD_DEFAULT_DATA.aspectRatio;
  // google requirement: close button must be visible when video covering content
  showCloseButton({ force: forceClose, removable: true });
}

function positionAdRelativeToContainer(placement, container) {
  const { left: scrollerLeft, top: scrollerTop } =
    activeRef.scroller?.getBoundingClientRect?.() || {
      left: 0,
      top: 0,
    };
  const { scrollTop: scrollerTopOffset } = activeRef.scroller || {
    scrollTop: 0,
  };
  const { left, top, width } = container?.getBoundingClientRect?.() || {
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  };
  placement.style.position = "absolute";
  placement.style.zIndex = String(VIDEO_AD_Z_INDEX || 0);
  placement.style.top = `${top - scrollerTop + scrollerTopOffset}px`;
  placement.style.right = "auto";
  placement.style.bottom = "auto";
  placement.style.left = `${left - scrollerLeft}px`;
  placement.style.transform = "none";
  placement.style.width = `${width}px`;
  placement.style.aspectRatio =
    container?.style.aspectRatio || VIDEO_AD_DEFAULT_DATA.aspectRatio;
  hideCloseButton();
}

function positionAdInContainer(placement, container) {
  const { width } = container.getBoundingClientRect();
  placement.style.width = `${width}px`;
  placement.style.position = "absolute";
  placement.style.zIndex = String(VIDEO_AD_Z_INDEX || 0);
  placement.style.top =
    placement.style.right =
    placement.style.bottom =
    placement.style.left =
      "auto";
  placement.style.transform = "none";
  placement.style.aspectRatio =
    container.style.aspectRatio || VIDEO_AD_DEFAULT_DATA.aspectRatio;
}

export function createPlaceholder(data) {
  if (!globals.document) return;
  const element = globals.document.createElement("div");
  element.className = VIDEO_AD_CLASS;
  const { ...hash }: VideoAdData = {
    ...VIDEO_AD_DEFAULT_DATA,
    ...data,
  };

  // assign remaining props to dataset
  for (const key in hash) {
    const value = hash[key];
    if (typeof value === "boolean") {
      if (value) element.dataset[key] = "1";
      continue;
    }
    if (key.startsWith("_") || key === "width" || key === "aspectRatio")
      continue;
    element.dataset[key] = hash[key];
  }

  return element;
}
